import { useEffect, useState } from "react";
import DropdownTreeSelect from "react-dropdown-tree-select";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { Button, Select, Switch, Text } from "@pentabd/ui";

import { ErrorModal, SelectControllerRow, SuccessModal } from "@components";
import { AssignGroupTable } from "./AssignGroupTable";
import { AssignPermissionTable } from "./AssignPermissionTable";

import { usePosition } from "@hooks/HRM/position/usePosition";
import { useBaseData } from "@hooks/useBaseData";
import { useLanguage } from "@hooks/useLanguage";
import { useMasterData } from "@hooks/useMasterData";

import {
  getConvertedData,
  getConvertedPositionData,
  getConvertedUnitData,
} from "@helpers/baseDataConvert";
import { getPositionTypeDataConvert } from "@helpers/positionTypeDataConvert";

import { BASE_DATA } from "@constants/base-data";
import { MASTER_DATA } from "@constants/master-data";
import { ROUTES } from "@constants/routes";
import { unitTreeDataConvert } from "@helpers/unitTreeDataConvert";

const {
  HRM: { POSITIONS },
} = ROUTES;
const { WINGS, DESIGNATIONS, UNIT_TREE } = BASE_DATA;
const { POST_TYPE } = MASTER_DATA;

export const ModifyPositionBody = () => {
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  const { t } = useTranslation();
  const { language } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  const methods = useForm();

  const {
    position,
    createPosition,
    isGetPositionRequest,

    isCreatePositionSuccess,
    isCreatePositionFailed,

    isUpdatePositionSuccess,
    isUpdatePositionFailed,

    updatePosition,
  } = usePosition(id ? Number(id) : null);

  const { data: designations } = useBaseData(DESIGNATIONS);
  const { data: wings } = useBaseData(WINGS);
  const { data: unitTree } = useBaseData(UNIT_TREE);
  const { data: postType } = useMasterData(POST_TYPE, {
    postTypeId: 113,
  });

  const closeErrorModal = () => setIsErrorModalOpen(false);

  const { handleSubmit, control, register, reset, setValue, watch } = methods;
  let wing = watch("wing") as string;

  const onSubmit = (data: any) => {
    if (id) updatePosition(data);
    else createPosition(data);
  };

  useEffect(() => {
    if (id) {
      let _permissions: any = {};

      if (position.permissionIds)
        for (let i = 0; i < position.permissionIds.length; i++) {
          _permissions[`${position.permissionIds[i].id}`] =
            position.permissionIds[i];
        }

      let _groups: any = {};

      if (position.groupIds)
        for (let i = 0; i < position.groupIds.length; i++) {
          _groups[`${position.groupIds[i].id}`] = position.groupIds[i];
        }

      const { id: unitId } = position.unit || {};
      const { id: positionId } = position.position || {};
      // const { isHead, isDeleted, isHired } = position;
      const { isHead, isDeleted } = position;

      reset({
        wing: unitId,
        designation: positionId,
        permissions: _permissions,
        groups: _groups,
        isDeleted,
        isHead,
        // isHired,
        postType: position.postType,
      });
    }
  }, [isGetPositionRequest, position, reset]);

  useEffect(() => {
    if (isCreatePositionSuccess) setIsSuccessModalOpen(true);
    else if (isCreatePositionFailed) setIsErrorModalOpen(true);
  }, [isCreatePositionFailed, isCreatePositionSuccess]);

  useEffect(() => {
    if (isUpdatePositionSuccess) setIsSuccessModalOpen(true);
    else if (isUpdatePositionFailed) setIsErrorModalOpen(true);
  }, [isUpdatePositionFailed, isUpdatePositionSuccess]);

  useEffect(() => {
    const reset = () => {
      setIsErrorModalOpen(false);
      setIsSuccessModalOpen(false);
    };
    return reset();
  }, []);

  return (
    <>
      {isSuccessModalOpen && (
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          redirectPath={POSITIONS}
          subtitle={`You have ${
            id ? "edited your Position" : "created a new Position"
          }`}
        />
      )}
      {isErrorModalOpen && (
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
        />
      )}
      <FormProvider {...methods}>
        <form
          className="d-flex flex-column gap-8"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="row">
            <div className="col-md-12 col-lg-3">
              <Text weight="semibold" size="sm" color="title">
                {t("POSITION.FORM.WING.LABEL")}
              </Text>
              <span className="text-danger">&#42;</span>
            </div>
            <div className="col-md-12 col-lg-7 d-flex gap-4">
              <Controller
                control={control}
                name="wing"
                render={({ field }) => {
                  return (
                    <DropdownTreeSelect
                      data={unitTreeDataConvert(
                        unitTree[0]?.children || [],
                        language,
                      )}
                      onChange={(e) => setValue("wing", e.id as number)}
                      mode="radioSelect"
                      texts={{
                        placeholder: t("POSITION.FORM.WING.PLACEHOLDER"),
                      }}
                    />
                  );
                }}
              />

              {wing && (
                <Controller
                  control={control}
                  name="wing"
                  render={({ field }) => {
                    return (
                      <Select
                        {...register("wing")}
                        options={getConvertedUnitData(wings, language)}
                        defaultValue={field.value as number}
                        disabled
                      />
                    );
                  }}
                />
              )}
            </div>
          </div>

          {wing && (
            <SelectControllerRow
              label="POSITION.FORM.DESIGNATION"
              control={control}
              name="designation"
              register={{ ...register("designation") }}
              options={getConvertedPositionData(designations, language)}
              setValue={setValue}
              required
            />
          )}
          <SelectControllerRow
            label="POSITION.FORM.POSITION_TYPE"
            control={control}
            name="postType"
            register={{ ...register("postType") }}
            options={getPositionTypeDataConvert(postType, language).map(
              (positionType: any) => ({
                ...positionType,
                customComponent: (
                  <Text
                    color={
                      (Number(positionType.code) === 1063 && "dark") ||
                      (Number(positionType.code) === 1064 && "success") ||
                      (Number(positionType.code) === 1065 && "info") ||
                      (Number(positionType.code) === 1066 && "danger") ||
                      "subtitle3"
                    }
                  >
                    {positionType.label}
                  </Text>
                ),
              }),
            )}
            setValue={setValue}
            required
          />

          <div className="row">
            <div className="col-md-12 col-lg-3">
              <Text weight="semibold" color="title" size="sm" component="p">
                {t("POSITION.FORM.POSITION_HEAD")}
              </Text>
            </div>
            <div className="col-md-12 col-lg-7">
              <Controller
                control={control}
                name="isHead"
                render={({ field }) => {
                  return (
                    <Switch
                      {...register("isHead")}
                      id="isHead"
                      name="groups"
                      value={field.value}
                      size="sm"
                      onChange={(e) => setValue("isHead", e.target.checked)}
                      controlling
                    />
                  );
                }}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-12 col-lg-3">
              <Text weight="semibold" color="title" size="sm" component="p">
                {t("POSITION.FORM.DEACTIVE")}
              </Text>
            </div>
            <div className="col-md-12 col-lg-7">
              <Controller
                control={control}
                name="isDeleted"
                render={({ field }) => {
                  return (
                    <Switch
                      {...register("isDeleted")}
                      id="isDeleted"
                      name="groups"
                      value={field.value}
                      size="sm"
                      onChange={(e) => setValue("isDeleted", e.target.checked)}
                      controlling
                    />
                  );
                }}
              />
            </div>
          </div>

          {/* <div className="row">
            <div className="col-md-12 col-lg-3">
              <Text weight="semibold" color="title" size="sm" component="p">
                {t("POSITION.FORM.HIRED")}
              </Text>
            </div>
            <div className="col-md-12 col-lg-7">
              <Controller
                control={control}
                name="isHired"
                render={({ field }) => {
                  return (
                    <Switch
                      {...register("isHired")}
                      id="isHired"
                      name="groups"
                      size="sm"
                      value={field.value}
                      onChange={(e) => setValue("isHired", e.target.checked)}
                      controlling
                    />
                  );
                }}
              />
            </div>
          </div> */}

          <div className="row">
            <div className="col-md-12 col-lg-3">
              <Text weight="semibold" color="title" size="sm" component="p">
                {t("POSITION.FORM.TABLE.ASSIGN_GROUP.LABEL")}
              </Text>
            </div>
            <div className="col-md-12 col-lg-7">
              <AssignGroupTable />
            </div>
          </div>
          <div className="row">
            <div className="col-md-12 col-lg-3">
              <Text weight="semibold" color="title" size="sm" component="p">
                {t("POSITION.FORM.TABLE.ASSIGN_PERMISSION.LABEL")}
              </Text>
            </div>
            <div className="col-md-12 col-lg-7 permission-table">
              <AssignPermissionTable />
            </div>
          </div>
          <div className="row border-top pt-8 mt-16">
            <div className="col-12 d-flex justify-content-end gap-6">
              <Button
                fill="outline"
                type="light"
                htmlType="button"
                onClick={() => navigate(-1)}
              >
                {t("POSITION.FORM.BUTTONS.CANCEL")}
              </Button>
              <Button fill="fill" type="primary" htmlType="submit">
                {t("POSITION.FORM.BUTTONS.SAVE")}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </>
  );
};
